import { useMemo } from 'react';

import { filterRows, isRowApproved } from './approvalQueueModel';
import { getBlackoutSearchText } from './tabs/blackoutDates/blackoutQueue';
import { getBundledExperienceSearchText } from './tabs/bundledExperiences/bundledExperiencesQueue';
import { getCampaignSearchText } from './tabs/campaign/campaignQueue';
import { getDatesRestrictionsSearchText } from './tabs/datesRestrictions/datesRestrictionsQueue';
import { getDealOptionsInclusionsSearchText } from './tabs/dealOptionsInclusions/dealOptionsInclusionsRules';
import { getCapacitySearchText, getMarginSearchText } from './tabs/egs/egsQueue';
import { getSetupSearchText } from './tabs/setup/setupQueue';
import { getSurchargeTableSearchText, getSurchargesSearchText } from './tabs/surcharges/surchargesQueue';
import { getTaxesPaymentsSearchText } from './tabs/taxesPayments/taxesPaymentsQueue';
import type { ChecklistCategoryId, ChecklistRow, ChecklistRowsByCategory, ManualApprovalState } from './types';

export interface VisibleChecklistRows {
  visibleBlackoutRows: ChecklistRowsByCategory['blackoutRows'];
  visibleBundledExperienceRows: ChecklistRowsByCategory['bundledExperienceRows'];
  visibleCampaignRows: ChecklistRowsByCategory['campaignRows'];
  visibleCapacityRows: ChecklistRowsByCategory['capacityRows'];
  visibleDatesRestrictionsRows: ChecklistRowsByCategory['datesRestrictionsRows'];
  visibleDealOptionsInclusionsRows: ChecklistRowsByCategory['dealOptionsInclusionsRows'];
  visibleMarginRows: ChecklistRowsByCategory['marginRows'];
  visibleSetupRows: ChecklistRowsByCategory['setupRows'];
  visibleSurchargeRules: ChecklistRowsByCategory['baseSurchargesRows'];
  visibleSurchargeTableRows: ChecklistRowsByCategory['surchargeTableRows'];
  visibleTaxesPaymentsRows: ChecklistRowsByCategory['taxesPaymentsRows'];
}

interface Input extends ChecklistRowsByCategory {
  activeCategoryId: ChecklistCategoryId;
  isApprovalQueueMode: boolean;
  manualApprovals: ManualApprovalState;
  searchQuery: string;
  showAutoApproved: boolean;
}

export function useVisibleChecklistRows({
  activeCategoryId,
  baseSurchargesRows,
  blackoutRows,
  bundledExperienceRows,
  campaignRows,
  capacityRows,
  datesRestrictionsRows,
  dealOptionsInclusionsRows,
  isApprovalQueueMode,
  manualApprovals,
  marginRows,
  searchQuery,
  setupRows,
  showAutoApproved,
  surchargeTableRows,
  taxesPaymentsRows,
}: Input): VisibleChecklistRows {
  const filterInput = useMemo(
    () => ({
      isApprovalQueueMode,
      manualApprovals,
      searchQuery,
      showAutoApproved,
    }),
    [isApprovalQueueMode, manualApprovals, searchQuery, showAutoApproved],
  );

  const visibleSetupRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'setup',
        rows: setupRows,
        text: getSetupSearchText,
      }),
    [activeCategoryId, filterInput, setupRows],
  );
  const visibleCampaignRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'campaign',
        rows: campaignRows,
        text: getCampaignSearchText,
      }),
    [activeCategoryId, campaignRows, filterInput],
  );
  const visibleDatesRestrictionsRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'datesRestrictions',
        rows: datesRestrictionsRows,
        text: getDatesRestrictionsSearchText,
      }),
    [activeCategoryId, datesRestrictionsRows, filterInput],
  );
  const visibleMarginRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'egs',
        rows: marginRows,
        text: getMarginSearchText,
      }),
    [activeCategoryId, filterInput, marginRows],
  );
  const visibleCapacityRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'egs',
        rows: capacityRows,
        text: getCapacitySearchText,
      }),
    [activeCategoryId, capacityRows, filterInput],
  );
  const visibleDealOptionsInclusionsRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'dealOptionsInclusions',
        rows: dealOptionsInclusionsRows,
        text: getDealOptionsInclusionsSearchText,
      }),
    [activeCategoryId, dealOptionsInclusionsRows, filterInput],
  );
  const visibleSurchargeTableRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'surcharges',
        rows: surchargeTableRows,
        text: getSurchargeTableSearchText,
      }),
    [activeCategoryId, filterInput, surchargeTableRows],
  );
  const visibleSurchargeRules = useMemo(
    () =>
      filterRows({
        isApproved: (row) => isRowApproved(row, manualApprovals),
        rows: isApprovalQueueMode ? baseSurchargesRows : [],
        searchQuery,
        showAutoApproved: false,
        text: getSurchargesSearchText,
      }),
    [baseSurchargesRows, isApprovalQueueMode, manualApprovals, searchQuery],
  );
  const visibleTaxesPaymentsRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'taxesPayments',
        rows: taxesPaymentsRows,
        text: getTaxesPaymentsSearchText,
      }),
    [activeCategoryId, filterInput, taxesPaymentsRows],
  );
  const visibleBlackoutRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'blackoutDates',
        rows: blackoutRows,
        text: getBlackoutSearchText,
      }),
    [activeCategoryId, blackoutRows, filterInput],
  );
  const visibleBundledExperienceRows = useMemo(
    () =>
      filterChecklistRows({
        ...filterInput,
        isActiveCategory: activeCategoryId === 'bundledExperiences',
        rows: bundledExperienceRows,
        text: getBundledExperienceSearchText,
      }),
    [activeCategoryId, bundledExperienceRows, filterInput],
  );

  return {
    visibleBlackoutRows,
    visibleBundledExperienceRows,
    visibleCampaignRows,
    visibleCapacityRows,
    visibleDatesRestrictionsRows,
    visibleDealOptionsInclusionsRows,
    visibleMarginRows,
    visibleSetupRows,
    visibleSurchargeRules,
    visibleSurchargeTableRows,
    visibleTaxesPaymentsRows,
  };
}

function filterChecklistRows<Row extends ChecklistRow>({
  isActiveCategory,
  isApprovalQueueMode,
  manualApprovals,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isActiveCategory: boolean;
  isApprovalQueueMode: boolean;
  manualApprovals: ManualApprovalState;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  return filterRows({
    isApproved: (row) => isRowApproved(row, manualApprovals),
    rows: isActiveCategory || isApprovalQueueMode ? rows : [],
    searchQuery,
    showAutoApproved: isApprovalQueueMode ? false : showAutoApproved,
    text,
  });
}
